import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import styles from "../TrendingKeywordsPreview.module.scss";
import { TableChart, TagCloudChart } from "src/components";
import { arrayInsert, formatToLocaleNumber } from "src/utils";
import { selectWidgetData } from "src/store/widgets/widgetsSelectors";
import { TableChartColumn } from "src/components/charts/TableChart/types";
import {
  selectTrackersEntities,
  selectTrackersCollectionById,
} from "src/store/selectors";

// Inner imports
import { WIDGET_ID } from "../constants";
import {
  renderKeywordValue,
  renderKeywordTracker,
  prepareTrendingKeywordsForTagCloud,
} from "../utils";

export const useTrendingKeywordsCharts = ({
  isExpanded,
  trackerIds,
  dashboardDateRangeId,
  trackersCollectionId,
}: Widget.ChartProps): Widget.Chart[] => {
  const { t } = useTranslation();

  const widgetData = useSelector((state: Store.RootState) =>
    selectWidgetData(state, dashboardDateRangeId, WIDGET_ID),
  );

  const trackersCollection = useSelector((state: Store.RootState) =>
    selectTrackersCollectionById(state, trackersCollectionId),
  );

  const trackers = useSelector(selectTrackersEntities);

  const trackersCollectionTrackerIds = useMemo<
    TrackersCollection.Data["trackerIds"]
  >(
    () => trackersCollection?.trackerIds || [],
    [trackersCollection?.trackerIds],
  );

  const formattedData = useMemo<Widgets.TrendingKeywords.KeywordData[]>(() => {
    if (!widgetData?.data) return [];

    const formattedData = new Set<Widgets.TrendingKeywords.KeywordData>();

    for (const trackerId of trackerIds) {
      const trackerData = widgetData.data.listChart[trackerId];

      if (!trackerData) continue;

      for (const data of trackerData) formattedData.add(data);
    }

    return [...formattedData].sort(
      (a, b) => b.trend - a.trend || b.volume - a.volume,
    );
  }, [widgetData?.data, trackerIds]);

  const tableColumns = useMemo<
    TableChartColumn<Widgets.TrendingKeywords.KeywordData>[]
  >(() => {
    const hasMultipleTrackers = trackersCollectionTrackerIds.length > 1;

    const columns: TableChartColumn<Widgets.TrendingKeywords.KeywordData>[] = [
      {
        key: "keyword",
        label: t("widget.trending_keywords.table.label.keyword"),
        style: { padding: "15px 0", textTransform: "capitalize" },
        valueFormatter: (value) =>
          renderKeywordValue(value, trackers, trackersCollectionTrackerIds),
      },
      {
        key: "trend",
        width: "20%",
        style: { padding: "15px 0", justifyContent: "flex-end" },
        label: t("widget.trending_keywords.table.label.search_trend"),
        valueFormatter: ({ trend }) => `${formatToLocaleNumber(trend)}%`,
      },
    ];

    if (isExpanded && hasMultipleTrackers)
      arrayInsert(columns, 1, {
        width: "25%",
        key: "trackerId",
        style: { padding: "15px 0" },
        label: t("widget.trending_keywords.table.label.tracker_name"),
        valueFormatter: (value) => renderKeywordTracker(value, trackers),
      });

    if (isExpanded)
      arrayInsert(columns, 2, {
        width: "20%",
        key: "volume",
        style: { padding: "15px 0", justifyContent: "flex-end" },
        label: t("widget.trending_keywords.table.label.search_volume"),
        valueFormatter: ({ volume }) => formatToLocaleNumber(volume),
      });

    return columns;
  }, [t, trackers, isExpanded, trackersCollectionTrackerIds]);

  return useMemo<Widget.Chart[]>(() => {
    const charts: Widget.Chart[] = [];

    if (formattedData.length) {
      const tagsData = prepareTrendingKeywordsForTagCloud(
        formattedData,
        trackersCollectionTrackerIds,
      );

      charts.push({
        type: "keywords",
        chart: (
          <TagCloudChart
            hasHoverValue
            tags={tagsData}
            tagStyle="filled"
            icon="TrendingWords"
            className={styles.cloudWrapper}
            tagClassName={styles.cloudKeyword}
            tagCloudClassName={styles.cloudKeywords}
            valueFormatter={(value) => `${value}%`}
          />
        ),
      });
    }

    if (formattedData.length)
      charts.push({
        type: "table",
        chart: (
          <TableChart
            hasHead={isExpanded}
            data={formattedData}
            columns={tableColumns}
            className={styles.tableWrapper}
            defaultSort={{ trend: "DESC" }}
          />
        ),
      });

    return charts;
  }, [formattedData, isExpanded, tableColumns, trackersCollectionTrackerIds]);
};
